import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const ACCESS_TOKEN = 'APEX2026';
const STORAGE_KEY = 'apex_access_granted';
const STORAGE_TTL = 24 * 60 * 60 * 1000; // 24 hours

export function useAccessGate() {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('access') === ACCESS_TOKEN) {
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
      return true;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && Date.now() - parseInt(stored, 10) < STORAGE_TTL) return true;
  } catch (_) { /* ignore */ }
  return false;
}

export function grantAccess() {
  try { localStorage.setItem(STORAGE_KEY, Date.now().toString()); } catch (_) { /* ignore */ }
}

const REASONS = [
  'Investor',
  'CISO / Security Leader',
  'Compliance / Governance',
  'Developer / Architect',
  'Partner',
  'Press',
  'Other',
];

const inputStyle = {
  width: '100%', background: '#080D1A',
  border: '1px solid #1E2A45', borderRadius: 8,
  padding: '10px 14px', fontSize: 13, color: '#F8FAFC',
  outline: 'none', fontFamily: 'inherit',
};
const labelStyle = {
  fontSize: 10, color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace',
  textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6, display: 'block',
};

export default function AccessRequestModal({ open, onClose, product = 'Apex Lens', onGranted }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [submittedName, setSubmittedName] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const accentColor = product === 'Apex Trust' ? '#6366F1' : '#0D9488';
  const formRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      setSubmitted(false);
      setErrorMsg('');
      setSubmittedName('');
      setSubmittedEmail('');
      setCode('');
      setCodeError('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setErrorMsg('');
    setSubmitting(true);

    const fd = new FormData(formRef.current);
    if (!fd.get('name')?.trim() || !fd.get('email')?.trim()) {
      setErrorMsg('Please enter your name and email address.');
      setSubmitting(false);
      return;
    }
    const payload = {
      product,
      name:         fd.get('name') || '',
      email:        fd.get('email') || '',
      organization: fd.get('organization') || '',
      reason:       fd.get('reason') || '',
      _subject:     `APEX Access Request: ${product} — ${fd.get('organization') || 'Unknown'}`,
      source:       'apex-access-gate',
    };

    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 12000);
    try {
      const res = await fetch('https://formspree.io/f/xnjwwanb', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(tid);
      if (!res.ok) throw new Error('endpoint_error');
      setSubmittedName(payload.name);
      setSubmittedEmail(payload.email);
      setSubmitted(true);
    } catch (_) {
      clearTimeout(tid);
      setErrorMsg('We could not submit that automatically. Please email hello@apexverity.io and we will respond within 24 hours.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleUnlock(e) {
    e.preventDefault();
    if (code.trim().toUpperCase() === ACCESS_TOKEN) {
      grantAccess();
      onGranted && onGranted();
    } else {
      setCodeError('Invalid access code.');
    }
  }

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="access-modal-title"
      data-testid="access-request-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div style={{
        background: '#0F1629', border: '1px solid #1E2A45',
        borderRadius: 20, padding: 36, maxWidth: 560, width: '100%',
        maxHeight: '90vh', overflowY: 'auto', position: 'relative',
        boxShadow: `0 0 0 1px ${accentColor}20, 0 32px 80px rgba(0,0,0,0.6)`,
      }}>
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="access-modal-close"
          style={{
            position: 'absolute', top: 14, right: 14,
            background: 'transparent', border: '1px solid #1E2A45',
            borderRadius: 8, width: 32, height: 32,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#94A3B8', cursor: 'pointer',
          }}
        >
          <X size={16} />
        </button>

        <p style={{ fontSize: 10, color: '#475569', fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.18em', margin: 0 }}>
          Request Access
        </p>
        <h2 id="access-modal-title" style={{ fontSize: 22, fontWeight: 700, marginTop: 6, color: accentColor, letterSpacing: '-0.01em' }}>
          {product.toUpperCase()}
        </h2>

        {submitted ? (
          <div data-testid="access-submitted">
            <p style={{ color: '#F8FAFC', fontSize: 15, marginTop: 18, fontWeight: 600 }}>Request received</p>
            <p style={{ color: '#94A3B8', fontSize: 13, marginTop: 10, lineHeight: 1.7 }}>
              Thank you, {submittedName || 'there'}. We will review your request and follow up at {submittedEmail || 'your email'}.
            </p>
            <p style={{ color: '#64748B', fontSize: 12, marginTop: 14, lineHeight: 1.7, fontStyle: 'italic' }}>
              If you have been pre-approved, you will receive an access link by email shortly. Otherwise you will hear from us within one business day.
            </p>
            <button
              onClick={onClose}
              data-testid="access-submitted-close"
              style={{
                marginTop: 22, background: 'transparent',
                border: `1px solid ${accentColor}`, color: accentColor,
                padding: '10px 18px', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontWeight: 600,
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <p style={{ color: '#94A3B8', fontSize: 13, marginTop: 14, lineHeight: 1.7 }}>
              This platform is in private preview. Access is reviewed before granting.
            </p>

            <form ref={formRef} onSubmit={handleSubmit} style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 14 }} noValidate data-testid="access-request-form">
              <div>
                <label style={labelStyle} htmlFor="ar-name">Name</label>
                <input id="ar-name" name="name" type="text" required style={inputStyle} autoComplete="name" />
              </div>
              <div>
                <label style={labelStyle} htmlFor="ar-email">Email</label>
                <input id="ar-email" name="email" type="email" required style={inputStyle} autoComplete="email" />
              </div>
              <div>
                <label style={labelStyle} htmlFor="ar-org">Organization</label>
                <input id="ar-org" name="organization" type="text" required style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle} htmlFor="ar-reason">What brings you here?</label>
                <select id="ar-reason" name="reason" required defaultValue="Investor" style={{ ...inputStyle, appearance: 'none' }}>
                  {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="access-request-submit"
                style={{
                  background: submitting ? `${accentColor}80` : accentColor,
                  color: 'white', border: 'none',
                  padding: '12px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                  cursor: submitting ? 'wait' : 'pointer',
                  marginTop: 4,
                }}
              >
                {submitting ? 'Sending…' : 'Request access →'}
              </button>

              {errorMsg && (
                <p style={{ color: '#F59E0B', fontFamily: 'JetBrains Mono, monospace', fontSize: 11, lineHeight: 1.6 }} data-testid="access-request-error">{errorMsg}</p>
              )}
            </form>

            {/* Access code section */}
            <div style={{ marginTop: 28, paddingTop: 22, borderTop: '1px solid #1E2A45' }}>
              <p style={{ fontSize: 11, color: '#475569', fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 12 }}>
                Already have access?
              </p>
              <form onSubmit={handleUnlock} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }} data-testid="access-unlock-form">
                <div style={{ flex: 1 }}>
                  <label style={labelStyle} htmlFor="ar-code">Enter your access code</label>
                  <input
                    id="ar-code" type="text" value={code} onChange={(e) => { setCode(e.target.value); setCodeError(''); }}
                    placeholder="APEX••••"
                    autoComplete="off"
                    data-testid="access-code-input"
                    style={{ ...inputStyle, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.18em', textTransform: 'uppercase' }}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="access-unlock-submit"
                  style={{
                    background: 'transparent', border: `1px solid ${accentColor}`,
                    color: accentColor, padding: '10px 16px', borderRadius: 10,
                    fontSize: 12, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
                  }}
                >
                  Unlock →
                </button>
              </form>
              {codeError && (
                <p style={{ color: '#F59E0B', fontSize: 11, marginTop: 8, fontFamily: 'JetBrains Mono, monospace' }} data-testid="access-code-error">
                  {codeError}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
